/**
 * Step1Qualifier — the qualifier step of the unified <LeadForm>.
 *
 * Renders ONE qualifier field decided by `variant.leadForm.qualifierField`:
 *   - 'sqm'      → numeric input (square meters)
 *   - 'quantity' → numeric input (units needed, e.g. parking spots)
 *   - 'when'     → select with StorageTime enum values
 *   - null       → render nothing; useEffect on mount auto-advances
 *
 * Component name kept for stability — file lives at steps/Step1Qualifier
 * but as of 2026-05-18 the step order is INVERTED: contact is now Step
 * 1, qualifier is now Step 2 (optional enrichment). The deal is already
 * pre-created by the time this renders, so the "Continuar" CTA always
 * advances regardless of whether the user typed an answer. The parent
 * fires `lead_form_enrich_submitted` with a `skipped` flag carrying
 * whatever value (or none) the renter provided.
 */

import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import BaseSelect from 'src/components/Inputs/BaseSelect'
import BaseTextField from 'src/components/Inputs/BaseTextField'
import type { LeadFormQualifier } from 'src/lib/listingVariants/types'
import { StorageTime } from 'src/types/graphql'

import type { LeadFormValues } from 'src/components/LeadForm/useLeadFormState'

type Step1QualifierProps = {
  qualifierField: LeadFormQualifier
  values: LeadFormValues
  setField: <K extends keyof LeadFormValues>(
    key: K,
    value: LeadFormValues[K]
  ) => void
  onContinue: () => void
}

const STORAGE_TIME_ORDER: ReadonlyArray<StorageTime> = [
  StorageTime.Month,
  StorageTime.FewMonths,
  StorageTime.FewMonthsSix,
  StorageTime.FewMonthsTwelve,
  StorageTime.Year,
  StorageTime.FewYears,
  StorageTime.Indefinitely
]

export default function Step1Qualifier({
  qualifierField,
  values,
  setField,
  onContinue
}: Step1QualifierProps) {
  const { t } = useTranslation('details')

  // Defensive: when variant has no qualifier, jump straight to success.
  // We do this with an effect (not during render) to avoid setState-during-
  // render warnings.
  useEffect(() => {
    if (qualifierField === null) {
      onContinue()
    }
    // We deliberately only re-run if `qualifierField` flips at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualifierField])

  if (qualifierField === null) {
    return null
  }

  // Continue ALWAYS advances — the deal was created at the contact step,
  // this enrichment is optional. The parent's handler reads the values
  // and tags `skipped` for analytics when the renter click-throughs
  // without typing.
  const handleContinue = () => {
    onContinue()
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Light enrich header — eyebrow + subtitle only, no big H3. The
          contact step (now Step 1) already carries the editorial framing
          for the form as a whole; this step is a quieter follow-up. The
          framing applies to all qualifier fields uniformly. */}
      <div className="flex flex-col gap-1.5">
        <p className="font-display text-[10px] tracking-[0.22em] text-blue-500 uppercase">
          {t('LEAD_FORM.ENRICH.EYEBROW')}
        </p>
        <p className="text-[13px] leading-relaxed text-ink-200">
          {t('LEAD_FORM.ENRICH.SUBTITLE')}
        </p>
      </div>

      {qualifierField === 'sqm' && (
        <BaseTextField
          topLabel={t('LEAD_FORM.STEP1.SQM_LABEL')}
          placeholder={t('LEAD_FORM.STEP1.SQM_PLACEHOLDER')}
          type="number"
          fullWidth
          value={values.sqm === null ? '' : String(values.sqm)}
          onChange={(event) => {
            const raw = (event.target as HTMLInputElement).value
            setField('sqm', raw === '' ? null : Number(raw))
          }}
          slotProps={{
            htmlInput: {
              min: 1,
              inputMode: 'numeric',
              className: 'text-base md:text-sm'
            }
          }}
        />
      )}

      {qualifierField === 'quantity' && (
        <BaseTextField
          topLabel={t('LEAD_FORM.STEP1.QUANTITY_LABEL')}
          placeholder={t('LEAD_FORM.STEP1.QUANTITY_PLACEHOLDER')}
          type="number"
          fullWidth
          value={values.quantity === null ? '' : String(values.quantity)}
          onChange={(event) => {
            const raw = (event.target as HTMLInputElement).value
            setField('quantity', raw === '' ? null : Number(raw))
          }}
          slotProps={{
            htmlInput: {
              min: 1,
              inputMode: 'numeric',
              className: 'text-base md:text-sm'
            }
          }}
        />
      )}

      {qualifierField === 'when' && (
        <BaseSelect
          value={values.when ?? ''}
          onChange={(event) => {
            const raw = event.target.value as string
            setField('when', raw.length === 0 ? null : raw)
          }}
          fullWidth
          displayEmpty
          entries={STORAGE_TIME_ORDER.reduce<{ [key: string]: string }>(
            (acc, key) => {
              acc[key] = t(`LEAD_FORM.STEP1.WHEN_OPTION_${key}`)
              return acc
            },
            {}
          )}
        />
      )}

      <button
        type="button"
        onClick={handleContinue}
        className="mt-2 w-full rounded-full bg-blue-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98]"
      >
        {t('LEAD_FORM.ENRICH.SUBMIT')}
      </button>
    </div>
  )
}
